import { get, isEmpty, toUpper } from 'lodash'
import { dateAndTimeConversion } from '../../../common/utils/dateAndTimeConversion'
import { AdditionalCPPODataKeys } from '../../../components/PrivateOfferForm/DetailedOffer/utils'
import type { DetailedOffer } from '../../../components/PrivateOfferForm/DetailedOffer/DetailedOffer'
import {
  ContractDurationType,
  DiscountType,
  type ResaleAuthorizationsResellerAgreements,
} from '../../modules/resaleAuthorizations'

export const getIsCustomPricing = (
  discountType?: DiscountType,
  contractDurationType?: ContractDurationType
) =>
  toUpper(discountType) === DiscountType.INDIVIDUAL_PRICING &&
  contractDurationType === ContractDurationType.CUSTOM

export const getResaleOfferForDetailedView = (
  agreement: ResaleAuthorizationsResellerAgreements,
  timeZone: string
) => {
  const { modifiedDate: agreementExpirationDate } = dateAndTimeConversion(
    agreement.agreementExpirationDate,
    timeZone
  )
  const offer: DetailedOffer = {
    errorMessage: agreement.error ?? '',
    createdAt: agreement.createdAt,
    offerStatus: agreement.offerStatus ?? agreement.status,
    dimensions: get(agreement, 'products[0].dimensions', []),
    installments: agreement.installmentInfo,
    awsAccountId: get(agreement, 'resellersInfo[0].resellerAwsAccountId', '-'),
    companyName:
      agreement?.companyName ||
      get(agreement, 'resellersInfo[0].resellerCompanyName', '-'),
    metaData: agreement?.metaData,
    offerRecipients: agreement?.agreementRecipients,
    offerName: agreement?.agreementName,
    emailCustomMessage: agreement?.emailCustomMessage,
    eulaType: agreement?.endCustomerEulaType,
    eulaFile: agreement?.endCustomerEulaFiles,
    offerDescription: agreement?.agreementDescription,
    flexiblePaymentTerms:
      toUpper(agreement?.discountType) === DiscountType.FLEXIBLE_PAYMENT_TERMS,
    isCustomPricing: getIsCustomPricing(
      agreement?.discountType,
      agreement?.contractDurationType
    ),
    renewal: agreement?.renewal || false,
    renewalChannel: agreement?.renewalChannel || null,
    additionalCPPOData: [
      {
        label: 'Discount details',
        key: AdditionalCPPODataKeys.DISCOUNT_TYPE,
        value: agreement?.discountType,
      },
      {
        label: 'Discount details',
        key: AdditionalCPPODataKeys.DISCOUNT_PERCENTAGE,
        value: agreement?.percentageDiscount || '',
      },
      {
        label: 'Contract details',
        key: AdditionalCPPODataKeys.CONTRACT_DURATION_TYPE,
        value: isEmpty(agreement?.discountType)
          ? ''
          : agreement?.contractDurationType,
      },
      {
        label: 'Contract details',
        key: AdditionalCPPODataKeys.CONTRACT_DURATION_IN_MONTHS_FOR_END_CUSTOMER,
        value: agreement?.contractDurationInMonthsForEndCustomer || '',
      },
      {
        label: 'Reseller license agreement details',
        key: AdditionalCPPODataKeys.RESELLER_LICENSE_AGREEMENT_TYPE,
        value: agreement?.resellerLicenseAgreementType,
      },
      {
        label: 'Buyer details',
        key: AdditionalCPPODataKeys.BUYER_AWS_ACCOUNT_ID,
        value: agreement?.buyerAwsAccountId,
      },
      {
        label: 'Reseller license agreement details',
        key: AdditionalCPPODataKeys.RESELLER_LICENSE_AGREEMENT_FILES,
        value: agreement?.resellerLicenseAgreementFiles ?? [],
      },
      {
        label: 'Agreement duration',
        key: AdditionalCPPODataKeys.AGREEMENT_DURATION_TYPE,
        value: agreement.agreementDurationType,
      },
      {
        label: 'Agreement duration',
        key: AdditionalCPPODataKeys.AGREEMENT_EXPIRATION_DATE,
        value: !isEmpty(agreement?.agreementExpirationDate)
          ? agreementExpirationDate
          : 'N.A.',
      },
    ],
    resellerName: agreement?.resellerName,
    stakeholders: agreement?.agreementStakeholders,
  }
  return offer
}
